import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import GridItem from 'components/Grid/GridItem.js';
import EVChargingService from '../../services/EVChargingService';
import useAPI from 'useAPI';

function formatBrand(brand) {
  if (!brand) return '';
  return brand
    .split(/[ _-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function dollars(cents) {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatDateParts(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

// Merge top-ups + debits into a single activity list ordered by
// created_at desc. Annotate each row with the icon/label/amount-sign
// the UI should render.
function buildActivity(walletResponse) {
  if (!walletResponse) return [];
  const topUps = (walletResponse.recent_top_ups || []).map((t) => ({
    kind: `top_up:${t.status}`,
    id: `t-${t.id}`,
    created_at: t.created_at,
    amount_cents: t.amount_cents, // positive
    receipt_url: t.stripe_receipt_url || null,
    failure_reason: t.failure_reason || null,
  }));
  const debits = (walletResponse.recent_debits || []).map((d) => ({
    kind: `debit:${d.kind}`,
    id: `d-${d.id}`,
    created_at: d.created_at,
    amount_cents: -d.amount_cents, // render as negative
    session_id: d.ev_charging_session_id || null,
    note: d.note || null,
    // STK-517: refund debits carry the originating top-up's Stripe
    // receipt URL when the backend can resolve it. Existing rows
    // without the join fall back to the placeholder no-op click.
    receipt_url: d.receipt_url || null,
  }));
  return [...topUps, ...debits].sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

function activityLabel(row) {
  switch (row.kind) {
    case 'top_up:succeeded': return 'Top-up';
    case 'top_up:pending': return 'Top-up (pending)';
    case 'top_up:voided': return 'Top-up (voided)';
    case 'top_up:failed':
      return row.failure_reason ? `Top-up failed — ${truncate(row.failure_reason, 60)}` : 'Top-up failed';
    case 'debit:session_debit': return 'Charging session';
    case 'debit:refund': return 'Refund';
    case 'debit:admin_credit': return row.note ? `Credit — ${truncate(row.note, 60)}` : 'Credit';
    default: return row.kind;
  }
}

function truncate(s, n) {
  if (!s || s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

export default function EVChargingWallet() {
  const navigate = useNavigate();
  const api = useAPI();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await EVChargingService.getWallet();
      setWallet(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Unable to load wallet.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (api) {
      EVChargingService.init(api);
      load();
    }
  }, [api, load]);

  const activity = buildActivity(wallet);
  const latestTopUp = wallet?.recent_top_ups?.[0];
  const showFailedBanner = latestTopUp && latestTopUp.status === 'failed';
  const autoTopUpPaused = wallet?.wallet && wallet.wallet.auto_top_up_enabled === false;

  return (
    <AuthenticatedContainer>
      <GridItem xs={12} sm={12} md={8} align='center'>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: 360,
            mx: 'auto',
            mt: 1,
          }}
        >
          <IconButton
            aria-label='Back to EV Charging'
            onClick={() => navigate('/ev-charging')}
            sx={{ position: 'absolute', left: 0 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h5' style={{ color: '#324152' }}>
            EV CHARGING WALLET
          </Typography>
        </Box>
      </GridItem>

      {loading && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        </GridItem>
      )}

      {error && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Alert severity='error' sx={{ mt: 2, maxWidth: 360, mx: 'auto' }}>
            {error}
          </Alert>
        </GridItem>
      )}

      {/* Empty state: no wallet (driver hasn't completed enrollment) */}
      {!loading && !error && wallet === null && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ maxWidth: 360, mx: 'auto', mt: 2, textAlign: 'left' }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              You don&apos;t have an EV Charging wallet yet. Complete
              enrollment to get started.
            </Typography>
            <Button
              variant='contained'
              color='primary'
              sx={{ mt: 2 }}
              onClick={() => navigate('/ev-charging')}
            >
              Go to EV Charging
            </Button>
          </Box>
        </GridItem>
      )}

      {!loading && !error && wallet && (
        <>
          {/* Balance card */}
          <GridItem xs={12} sm={12} md={8} align='center'>
            <Paper
              sx={{
                maxWidth: 360,
                mx: 'auto',
                mt: 2,
                px: 3,
                py: 2,
                textAlign: 'center',
                backgroundColor: '#e8f5e9',
                border: '1px solid #c8e6c9',
              }}
            >
              <Typography
                variant='caption'
                sx={{ display: 'block', color: '#1b5e20', fontWeight: 'bold', letterSpacing: '0.5px' }}
              >
                BALANCE
              </Typography>
              <Typography variant='h4' sx={{ color: '#1b5e20', mt: 0.5 }}>
                {dollars(wallet.wallet.balance_cents)}
              </Typography>
              <Typography
                variant='caption'
                sx={{ display: 'block', mt: 1, color: autoTopUpPaused ? 'warning.dark' : 'text.secondary' }}
              >
                {autoTopUpPaused
                  ? 'Auto-top up is paused.'
                  : `Auto-top up when balance drops below ${dollars(wallet.wallet.auto_top_up_threshold_cents)}.`}
              </Typography>
            </Paper>
          </GridItem>

          {/* Failed top-up banner */}
          {showFailedBanner && (
            <GridItem xs={12} sm={12} md={8} align='center'>
              <Alert
                severity='error'
                sx={{ mt: 2, maxWidth: 360, mx: 'auto', textAlign: 'left' }}
              >
                Your last top-up failed
                {latestTopUp.failure_reason ? `: ${latestTopUp.failure_reason}` : ''}.
                {' '}
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => navigate('/ev-charging/payment')}
                >
                  Update your card
                </span>{' '}
                to resume charging.
              </Alert>
            </GridItem>
          )}

          {/* Saved card */}
          <GridItem xs={12} sm={12} md={8} align='center'>
            <Box
              sx={{
                maxWidth: 360,
                mx: 'auto',
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {wallet.card
                  ? `${formatBrand(wallet.card.brand)} •• ${wallet.card.last4}`
                  : 'No card on file'}
              </Typography>
              <Button
                variant='outlined'
                size='small'
                onClick={() => navigate('/ev-charging/payment')}
              >
                {wallet.card ? 'Replace card' : 'Add card'}
              </Button>
            </Box>
          </GridItem>

          {/* Recent activity */}
          <GridItem xs={12} sm={12} md={8} align='center'>
            <Box sx={{ maxWidth: 360, mx: 'auto', mt: 3, textAlign: 'left' }}>
              <Typography
                variant='caption'
                sx={{ display: 'block', color: 'text.secondary', fontWeight: 'bold', letterSpacing: '0.5px', mb: 0.5 }}
              >
                RECENT ACTIVITY
              </Typography>
              {activity.length === 0 ? (
                <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
                  No activity yet.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {activity.map((row, idx) => {
                    const negative = row.amount_cents < 0;
                    // Top-ups and refunds both deep-link to the Stripe
                    // receipt page. Refund URLs come from the offsetting
                    // top-up — populated by step 12's charge.refunded
                    // webhook (not yet built); until then refunds get a
                    // placeholder click that does nothing.
                    const linkLike =
                      row.kind === 'top_up:succeeded' ||
                      row.kind === 'debit:refund';
                    const onClick = linkLike
                      ? () => {
                          if (row.receipt_url) {
                            window.open(row.receipt_url, '_blank', 'noopener,noreferrer');
                          }
                        }
                      : undefined;
                    const { date, time } = formatDateParts(row.created_at);
                    return (
                      <React.Fragment key={row.id}>
                        {idx > 0 && <Divider component='li' />}
                        <ListItem
                          disableGutters
                          sx={{
                            cursor: onClick ? 'pointer' : 'default',
                            py: 0.75,
                            alignItems: 'center',
                          }}
                          onClick={onClick}
                        >
                          <Box sx={{ minWidth: 56, mr: 1.5, flexShrink: 0 }}>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                            >
                              {date}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                            >
                              {time}
                            </Typography>
                          </Box>
                          <Typography
                            variant='body2'
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: linkLike ? 'primary.main' : 'text.primary',
                              textDecoration: linkLike ? 'underline' : 'none',
                            }}
                          >
                            {activityLabel(row)}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              color: negative ? 'error.main' : 'success.main',
                              fontWeight: 'bold',
                              ml: 1,
                              minWidth: 64,
                              textAlign: 'right',
                              flexShrink: 0,
                            }}
                          >
                            {dollars(row.amount_cents)}
                          </Typography>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => navigate('/ev-charging/statements')}
                >
                  View full statement
                </Button>
              </Box>
            </Box>
          </GridItem>
        </>
      )}
    </AuthenticatedContainer>
  );
}
